import axios from 'axios';
import * as turf from "@turf/turf";
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { wfsCache, debounce, bboxOverlap, requestManager } from '../../utils/wfsCache';
import { createSingleAssayPopupTable } from './AssayOverlay';

import {
    Popup,
    Source,
    Layer,
    useMap
} from 'react-map-gl/maplibre';

const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER,
    timeout: 30000
});

// Debounced query function
export const debouncedQueryAssayWFS = debounce(queryAssayWFSOptimized, 300);

/**
 * Build a CQL clause filtering on gswa_load_date (an xsd:dateTime attribute in
 * GeoServer) from the month/year range in assayFilters.
 *
 * dateFrom / dateTo are 'YYYY-MM' month strings. The range is inclusive of both
 * selected months: the lower bound is the first instant of dateFrom's month and
 * the upper bound is the first instant of the month AFTER dateTo (exclusive), so
 * the whole of dateTo's month is covered.
 *
 * Samples with a null gswa_load_date are always included while the filter is
 * active, so enabling the filter never silently drops undated samples.
 *
 * Returns '' (no clause) when the filter is disabled or no bound is set.
 */
function buildLoadDateClause(assayFilters) {
    if (!assayFilters.dateFilterEnabled) return '';

    const { dateFrom, dateTo } = assayFilters;
    if (!dateFrom && !dateTo) return '';

    // 'YYYY-MM' -> Date at the first instant (UTC) of that month, optionally
    // advancing by `monthOffset` whole months first. GeoServer's ECQL temporal
    // parser only accepts yyyy-MM-ddTHH:mm:ssZ, so strip the .SSS milliseconds
    // that Date.toISOString() emits (a fractional second makes the whole clause
    // fail to parse and the WFS query error out).
    const monthStartIso = (ym, monthOffset = 0) => {
        const [y, m] = ym.split('-').map(Number);
        // m is 1-based; Date month arg is 0-based, so (m - 1) + offset.
        return new Date(Date.UTC(y, (m - 1) + monthOffset, 1))
            .toISOString().replace(/\.\d{3}Z$/, 'Z');
    };

    const conditions = [];
    if (dateFrom) conditions.push(`gswa_load_date >= ${monthStartIso(dateFrom)}`);
    if (dateTo) conditions.push(`gswa_load_date < ${monthStartIso(dateTo, 1)}`);

    // Wrap the range so nulls can be OR'd in without being consumed by the
    // outer AND that joins this clause to the rest of the filter.
    return `and (( ${conditions.join(' and ')} ) or gswa_load_date is null)`;
}

/**
 * Optimized assay WFS query with caching and request management
 */
export function queryAssayWFSOptimized(bboxString, setStateGeoJSON, assayFilters, retryCount = 0) {
    if (assayFilters.anumber !== 0) return Promise.resolve();

    const cacheKey = wfsCache.generateKey(bboxString, assayFilters, 'assays');
    
    // Check cache first
    const cachedResult = wfsCache.get(cacheKey);
    if (cachedResult) {
        setStateGeoJSON(cachedResult);
        return Promise.resolve(cachedResult);
    }

    // Create abort controller for this request
    const controller = requestManager.createController('assays');

    const assayTypeArr = assayFilters.type.map(e => e.value);

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        and
        (
        au_ppb BETWEEN ${assayFilters.ppbmt} AND ${assayFilters.ppblt}
        ${assayFilters.nullValues ? 'OR au_ppb is null' : ''}
        )
        and
        surfacesampletype in ('${assayTypeArr.join(`','`)}')
        ${buildLoadDateClause(assayFilters)}
    `;

    return GoldFinderGeoserver.get("/ne/ows?", {
        signal: controller.signal,
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_au_soil_assays',
            maxFeatures: 99999,
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    }).then((response) => {
        // Cache the result
        wfsCache.set(cacheKey, response.data);
        setStateGeoJSON(response.data);
        return response.data;
    }).catch((error) => {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
            console.error('Assay WFS query error:', error);
        }
        
        // Don't re-throw canceled errors
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return null;
        }
        
        // Retry on timeout or network errors
        const maxRetries = 10;
        const baseDelay = 1000;
        if ((error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') && retryCount < maxRetries) {
            const delay = baseDelay * Math.pow(2, retryCount);
            console.log(`Retrying assay query in ${delay}ms (attempt ${retryCount + 1}/${maxRetries + 1})`);
            
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(queryAssayWFSOptimized(bboxString, setStateGeoJSON, assayFilters, retryCount + 1));
                }, delay);
            });
        }
        
        // For other errors or max retries exceeded, return empty GeoJSON to keep UI functional
        const emptyResult = {
            type: "FeatureCollection",
            features: []
        };
        
        // Set empty result in state to prevent UI breakage
        setStateGeoJSON(emptyResult);
        
        // Return empty result instead of throwing error
        return emptyResult;
    });
}

/**
 * Optimized AssayLayers component with memoization
 */
function AssayLayersOptimized({ assayMarkerColourSettings, overlayVisibility }) {
    const orderedAssayMarkerArray = useMemo(() => 
        assayMarkerColourSettings.toSorted((a, b) => a.moreThan - b.moreThan),
        [assayMarkerColourSettings]
    );

    const layers = useMemo(() => {
        const layerConfigs = [];

        for (let i = 0; i < orderedAssayMarkerArray.length; i++) {
            const next = i + 1;
            const config = orderedAssayMarkerArray[i];

            const layerConfig = {
                id: config.id,
                source: 'soilsamples',
                type: 'circle',
                minzoom: 10.6,
                paint: { 
                    'circle-color': config.color, 
                    'circle-radius': 6,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#ffffff'
                },
                filter: ['all',
                    ['>=', 'au_ppb', config.moreThan],
                    ['<', 'au_ppb', i !== orderedAssayMarkerArray.length - 1 ? orderedAssayMarkerArray[next].moreThan : 9999]
                ],
                layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
            };

            layerConfigs.push(layerConfig);
        }

        return layerConfigs;
    }, [orderedAssayMarkerArray, overlayVisibility.soils]);

    return (
        <>
            {layers.map((layerConfig) => (
                <Layer key={layerConfig.id} {...layerConfig} />
            ))}
        </>
    );
}

/**
 * Optimized AssayOverlay component
 */
export function AssayOverlayMapLibreOptimized({ 
    overlayVisibility, 
    assayMarkerColourSettings, 
    assayGeoJson, 
    setAssayGeojson, 
    assayFilters 
}) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "assay_samples",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4326" } },
        "features": []
    };

    const { goldfindermap } = useMap();
    const lastBboxRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    // Memoize background layer config
    const backgroundConfig = useMemo(() => ({
        id: 'assay-background-config',
        source: 'soilsamples',
        minzoom: 10.6,
        type: 'circle',
        paint: { 
            'circle-color': '#f9f9f9', 
            'circle-radius': 7,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#e0e0e0'
        },
        layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
    }), [overlayVisibility.soils]);

    // Optimized query function
    const queryAssays = useCallback(async (bbox) => {
        if (!goldfindermap || goldfindermap.getZoom() <= 10.6) return;

        // Check if bbox has changed significantly
        if (lastBboxRef.current && bboxOverlap(bbox, lastBboxRef.current)) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await debouncedQueryAssayWFS(bbox, setAssayGeojson, assayFilters);
            if (result !== null) {
                lastBboxRef.current = bbox;
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                console.error('Error querying assays:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [goldfindermap, assayFilters, setAssayGeojson]);

    // Effect for filter changes
    useEffect(() => {
        if (!goldfindermap) return;
        
        // Clear cache when filters change
        wfsCache.clearLayer('assays');
        
        if (goldfindermap.getZoom() > 10.6) {
            const bounds = goldfindermap.getBounds();
            const wfsbbox = [
                bounds.getSouthWest().lng, 
                bounds.getSouthWest().lat, 
                bounds.getNorthEast().lng, 
                bounds.getNorthEast().lat
            ].join();
            
            queryAssays(wfsbbox);
        }
    }, [assayFilters, queryAssays]);

    return (
        <Source id="soilsamples" type="geojson" data={assayGeoJson}>
            <Layer {...backgroundConfig} />
            <AssayLayersOptimized 
                overlayVisibility={overlayVisibility} 
                assayMarkerColourSettings={assayMarkerColourSettings} 
            />
        </Source>
    );
}